import { useEffect, useState, useCallback, useRef } from 'react';
import { Autocomplete } from '@material-ui/lab';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { RebacExplorerClient, FgaTypeDefinition, CheckResponse } from '../../api/RebacExplorerClient';

const DEBOUNCE_MS = 300;
const USER_SEARCH_LIMIT = 20;
const OBJECT_SEARCH_LIMIT = 20;

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function buildRelations(types: FgaTypeDefinition[], objectType: string): string[] {
  const t = types.find(d => d.type === objectType);
  return Object.keys(t?.relations ?? {});
}

function typeFromRef(ref: string): string {
  return ref.split(':')[0] ?? '';
}

// ─── User Autocomplete ────────────────────────────────────────────────────────

function UserAutocomplete({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const catalogApi = useApi(catalogApiRef);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedInput = useDebounce(inputValue, DEBOUNCE_MS);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    catalogApi
      .queryEntities({
        filter: { kind: 'User' },
        // FLWP-71232: sem `fields` aqui, o catalog-backend cai no default
        // 'metadata.uid' como único campo pesquisado (o UUID interno da
        // entidade) — a busca nunca batia com o texto digitado.
        fullTextFilter: {
          term: debouncedInput,
          fields: ['metadata.name', 'spec.profile.email', 'spec.profile.displayName'],
        },
        fields: ['metadata.name', 'metadata.namespace'],
        limit: USER_SEARCH_LIMIT,
      })
      .then(resp => {
        if (ctrl.signal.aborted) return;
        setOptions(
          resp.items
            .map(e => `user:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`)
            .sort(),
        );
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setOptions([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => ctrl.abort();
  }, [catalogApi, debouncedInput]);

  return (
    <Autocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => {
        if (reason !== 'reset') setInputValue(v);
      }}
      onChange={(_, v) => { onChange(v); setInputValue(v ?? ''); }}
      loading={loading}
      filterOptions={x => x}
      renderInput={params => (
        <TextField
          {...params}
          label="User"
          variant="outlined"
          size="small"
          placeholder="Digite para buscar…"
          helperText={
            value
              ? <span style={{ fontFamily: 'monospace' }}>{value}</span>
              : `Busca server-side — máx. ${USER_SEARCH_LIMIT} resultados`
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={option => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{option}</span>
      )}
      style={{ marginBottom: 16 }}
      noOptionsText={inputValue.length === 0 ? 'Digite para buscar usuários' : 'Nenhum usuário encontrado'}
    />
  );
}

// ─── Object Autocomplete ──────────────────────────────────────────────────────

function ObjectAutocomplete({
  objectTypes,
  value,
  onChange,
  client,
}: {
  objectTypes: string[];
  value: string | null;
  onChange: (v: string | null) => void;
  client: RebacExplorerClient;
}) {
  const [selectedType, setSelectedType] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedInput = useDebounce(inputValue, DEBOUNCE_MS);
  const abortRef = useRef<AbortController | null>(null);

  // Inicializa selectedType quando o modelo chega (objectTypes começa vazio)
  useEffect(() => {
    if (selectedType === '' && objectTypes.length > 0) {
      setSelectedType(objectTypes[0]);
    }
  }, [objectTypes, selectedType]);

  // Quando o tipo muda, limpa seleção e input
  useEffect(() => {
    if (!selectedType) return;
    onChange(null);
    setInputValue('');
    setOptions([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  useEffect(() => {
    if (!selectedType) return undefined;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    client
      .getObjects({ type: selectedType, q: debouncedInput, limit: OBJECT_SEARCH_LIMIT })
      .then(objs => {
        if (ctrl.signal.aborted) return;
        setOptions(objs);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setOptions([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => ctrl.abort();
  }, [client, selectedType, debouncedInput]);

  return (
    <Box style={{ marginBottom: 24 }}>
      <Box display="flex" style={{ gap: 12 }}>
        {/* Seletor de tipo */}
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value as string)}
            label="Tipo"
          >
            {objectTypes.map(t => (
              <MenuItem key={t} value={t}>
                <span style={{ fontFamily: 'monospace' }}>{t}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Autocomplete de ID */}
        <Autocomplete
          style={{ flex: 1 }}
          options={options}
          value={value}
          inputValue={inputValue}
          onInputChange={(_, v, reason) => {
            if (reason !== 'reset') setInputValue(v);
          }}
          onChange={(_, v) => { onChange(v); setInputValue(v ?? ''); }}
          loading={loading}
          filterOptions={x => x}
          renderInput={params => (
            <TextField
              {...params}
              label="Object ID"
              variant="outlined"
              size="small"
              placeholder="Digite para buscar…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={14} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={option => (
            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{option}</span>
          )}
          noOptionsText={inputValue.length === 0 ? 'Digite para buscar objetos' : 'Nenhum objeto encontrado'}
        />
      </Box>
      {value && (
        <Typography variant="caption" style={{ fontFamily: 'monospace', color: '#555', marginTop: 4, display: 'block' }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

// ─── CheckPanel ───────────────────────────────────────────────────────────────

export function CheckPanel({ client }: { client: RebacExplorerClient }) {
  const theme = useTheme();
  const [types, setTypes] = useState<FgaTypeDefinition[]>([]);
  const [loadingModel, setLoadingModel] = useState(true);

  const [userRef, setUserRef] = useState<string | null>(null);
  const [objectRef, setObjectRef] = useState<string | null>(null);
  const [relation, setRelation] = useState('');

  const [result, setResult] = useState<CheckResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client.getModel()
      .then(data => setTypes(data.model?.type_definitions ?? []))
      .catch(() => {})
      .finally(() => setLoadingModel(false));
  }, [client]);

  // Tipos de objeto (exclui "user" — usuários vêm do catalog)
  const objectTypes = types.map(t => t.type).filter(t => t !== 'user');

  // Relações disponíveis para o tipo do objeto selecionado
  const relationOptions = objectRef
    ? buildRelations(types, typeFromRef(objectRef))
    : [];

  // Sincroniza relation quando o objeto muda
  useEffect(() => {
    if (!objectRef) return;
    const rels = buildRelations(types, typeFromRef(objectRef));
    setRelation(rels[0] ?? '');
  }, [objectRef, types]);

  const canSubmit = !!userRef && !!objectRef && !!relation && !checking;

  const handleCheck = useCallback(async () => {
    if (!canSubmit || !userRef || !objectRef) return;
    setChecking(true);
    setResult(null);
    setError(null);
    try {
      setResult(await client.check({ user: userRef, relation, object: objectRef }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  }, [client, userRef, relation, objectRef, canSubmit]);

  if (loadingModel) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 12, padding: 24 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">Carregando modelo…</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 24, maxWidth: 700 }}>
      <Typography variant="h6" gutterBottom>Check Permission</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Consulta read-only ao OpenFGA. Nenhuma tupla é criada ou removida.
      </Typography>

      <UserAutocomplete
        value={userRef}
        onChange={v => { setUserRef(v); setResult(null); }}
      />

      <ObjectAutocomplete
        objectTypes={objectTypes}
        value={objectRef}
        onChange={v => { setObjectRef(v); setResult(null); }}
        client={client}
      />

      <FormControl
        variant="outlined"
        size="small"
        fullWidth
        style={{ marginBottom: 16 }}
        disabled={relationOptions.length === 0}
      >
        <InputLabel>Relation</InputLabel>
        <Select
          value={relation}
          onChange={e => { setRelation(e.target.value as string); setResult(null); }}
          label="Relation"
        >
          {relationOptions.map(r => (
            <MenuItem key={r} value={r}>
              <span style={{ fontFamily: 'monospace' }}>{r}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleCheck}
        disabled={!canSubmit}
        startIcon={checking ? <CircularProgress size={16} /> : undefined}
        style={{ marginBottom: 24 }}
      >
        {checking ? 'Checking…' : 'Check'}
      </Button>

      {error && (
        <Box
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            borderRadius: 4,
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(211,47,47,0.15)' : '#ffebee',
            border: `1px solid ${theme.palette.error.main}`,
          }}
        >
          <Typography variant="body2" style={{ color: theme.palette.error.main, fontWeight: 600 }}>
            Erro: {error}
          </Typography>
        </Box>
      )}

      {result && (
        <Paper
          elevation={0}
          style={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            borderRadius: 4,
            border: result.allowed ? `1px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.error.main}`,
            backgroundColor: (() => {
              if (theme.palette.type === 'dark') {
                return result.allowed ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)';
              }
              return result.allowed ? '#f1f8e9' : '#ffebee';
            })(),
          }}
        >
          {result.allowed
            ? <CheckCircleOutlineIcon style={{ color: theme.palette.success.main, fontSize: 32 }} />
            : <CancelOutlinedIcon style={{ color: theme.palette.error.main, fontSize: 32 }} />
          }
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {result.allowed ? 'Allowed' : 'Denied'}
            </Typography>
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {userRef} {relation} {objectRef}
            </Typography>
            <Chip size="small" label={`${result.latencyMs} ms`} style={{ marginTop: 4 }} />
          </Box>
        </Paper>
      )}
    </Box>
  );
}
